import { useCallback, useState } from 'react';
import { MAX_FILE_SIZE_BYTES } from '@brighte/validation';
import { datadogRum } from '@datadog/browser-rum';
import { css } from '@emotion/css';
import {
  PropertyTitleOwnerOutput,
  useCreateTitleOwnerDocumentMutation,
  useSubmitTitleOwnerIncomeMutation,
  useUpdateTitleOwnerDocumentStatusMutation,
  useVerifyTitleOwnerIncomeDocumentsMutation,
} from '@graphql/index';
import { useUploadFilesToS3 } from '@hooks';
import { Accordion, AccordionItem } from '@spark-web/accordion';
import { Alert } from '@spark-web/alert';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Checkbox } from '@spark-web/checkbox';
import { Dropzone } from '@spark-web/dropzone';
import { Field } from '@spark-web/field';
import { Heading } from '@spark-web/heading';
import { CheckCircleIcon, ExclamationIcon } from '@spark-web/icon';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { TextLink } from '@spark-web/text-link';
import EligibilityStepHeader from './EligibilityStepHeader';

type NoaVerificationErrorCode =
  | 'UNKNOWN_DOCUMENT_TYPE'
  | 'DOCUMENT_TYPE_MISMATCH'
  | 'NOTICE_OF_ASSESSMENT_OUT_OF_DATE'
  | 'NOTICE_OF_ASSESSMENT_NO_YEAR'
  | 'DOCUMENT_UNREADABLE';

type NoaErrorContent = { description?: string };

const NOA_VERIFICATION_ERROR_CONTENT: Record<NoaVerificationErrorCode, NoaErrorContent> = {
  UNKNOWN_DOCUMENT_TYPE: {
    description:
      'The Notice of Assessment could not be verified. Please check that you uploaded the correct document and try again.',
  },
  DOCUMENT_TYPE_MISMATCH: {
    description:
      'The Notice of Assessment could not be verified. Please check that you uploaded the correct document and try again.',
  },
  NOTICE_OF_ASSESSMENT_OUT_OF_DATE: {
    description: 'Please upload the assessment for either FY24 or FY25.',
  },
  NOTICE_OF_ASSESSMENT_NO_YEAR: {
    description:
      'The Notice of Assessment could not be verified. Please check that you uploaded the correct document and try again.',
  },
  DOCUMENT_UNREADABLE: {
    description:
      'The Notice of Assessment could not be verified. Please check that you uploaded the correct document and that any password protections are removed.',
  },
};

type VerifyDeclaredIncomeProps = {
  owners: PropertyTitleOwnerOutput[];
  declaredIncomes: Record<string, string>;
  trackingId: string;
  productName: string;
  applicantName: string;
  onConfirm: () => void;
  onBack: () => void;
};

type OwnerVerificationStatus =
  | 'idle'
  | 'uploading'
  | 'passed'
  | 'failed_once'
  | 'failed_twice'
  | 'failed_final'
  | 'zero_income_skip';

function formatMoney(amount: string): string {
  return `$${Number(amount).toLocaleString('en-AU')}`;
}

function getInitials(fullName: string): string {
  return fullName
    .split(' ')
    .slice(0, 2)
    .map(word => word[0]?.toUpperCase() ?? '')
    .join('');
}

const AVATAR_BACKGROUNDS: Array<'primaryLow' | 'infoLow'> = ['primaryLow', 'infoLow'];

type OwnerCardProps = {
  owner: PropertyTitleOwnerOutput;
  declaredIncome: string;
  trackingId: string;
  avatarBackground: 'primaryLow' | 'infoLow';
  isApplicant: boolean;
  onConsentChange: (ownerId: string, consented: boolean) => void;
  onStatusChange: (ownerId: string, status: OwnerVerificationStatus) => void;
};

function OwnerVerificationCard({
  owner,
  declaredIncome,
  trackingId,
  avatarBackground,
  isApplicant,
  onConsentChange,
  onStatusChange,
}: OwnerCardProps) {
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState<OwnerVerificationStatus>('idle');
  const [verifyErrorCode, setVerifyErrorCode] = useState<NoaVerificationErrorCode | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadError, setUploadError] = useState<string | undefined>();
  const [tooManyFilesError, setTooManyFilesError] = useState(false);
  const [zeroIncomeConfirmed, setZeroIncomeConfirmed] = useState(false);
  // Tracks files already create+uploaded+status'd so a retry skips them instead of duplicating.
  const [uploadedDocIds, setUploadedDocIds] = useState<Map<File, string>>(new Map());

  const isZeroIncome = Number(declaredIncome) === 0;

  const { mutateAsync: createDocument } = useCreateTitleOwnerDocumentMutation({ useErrorBoundary: false });
  const { mutateAsync: uploadToS3 } = useUploadFilesToS3();
  const { mutateAsync: updateDocumentStatus } = useUpdateTitleOwnerDocumentStatusMutation({ useErrorBoundary: false });
  const { mutateAsync: verifyDocuments } = useVerifyTitleOwnerIncomeDocumentsMutation({ useErrorBoundary: false });

  const updateStatus = (next: OwnerVerificationStatus) => {
    setStatus(next);
    onStatusChange(owner.id, next);
  };

  const MAX_FILES = 2;

  const handleFileDrop = (event: { target: { value: unknown } }) => {
    const value = event?.target?.value;
    const files = Array.isArray(value) ? (value as File[]) : [];
    setUploadError(undefined);
    // A fresh selection starts a new upload — forget any previously-uploaded docs.
    setUploadedDocIds(new Map());
    if (files.length > MAX_FILES) {
      setTooManyFilesError(true);
      setSelectedFiles([]);
      return;
    }
    setTooManyFilesError(false);
    setSelectedFiles(files);
  };

  const handleUploadAndVerify = async () => {
    if (selectedFiles.length === 0) return;

    const previousStatus = status;
    updateStatus('uploading');
    setUploadError(undefined);
    setVerifyErrorCode(null);

    try {
      // Carry forward files uploaded on a previous attempt so a retry resumes the rest.
      const currentUploaded = new Map(uploadedDocIds);
      let failedCount = 0;

      for (const file of selectedFiles) {
        if (currentUploaded.has(file)) continue;

        try {
          const createResult = await createDocument({
            input: {
              applicationId: trackingId,
              titleSearchOwnerId: owner.id,
              filename: file.name,
              fileSize: file.size,
            },
          });

          const { document, url, fieldsJson } = createResult.createTitleOwnerDocument;

          if (!url || !fieldsJson) {
            throw new Error('Failed to get presigned upload URL');
          }

          const uploadMetaData = JSON.parse(fieldsJson);
          const formData = new FormData();
          Object.keys(uploadMetaData).forEach(key => formData.append(key, uploadMetaData[key]));
          formData.append('file', file);

          await uploadToS3({ bucketUrl: url, file: formData });

          await updateDocumentStatus({
            input: {
              applicationId: trackingId,
              titleSearchOwnerId: owner.id,
              documentId: document.id,
            },
          });

          currentUploaded.set(file, document.id);
        } catch {
          failedCount++;
        }
      }

      setUploadedDocIds(currentUploaded);

      if (failedCount > 0) {
        const succeeded = currentUploaded.size;
        const total = selectedFiles.length;

        if (succeeded > 0) {
          setUploadError('Few files failed to upload. Please try again.');
          datadogRum.addAction('LandOwner Document Partial Upload Error', { trackingId, succeeded, total });
        } else {
          setUploadError('Something went wrong uploading your document. Please try again.');
          datadogRum.addAction('LandOwner Document Upload Error', { trackingId });
        }

        updateStatus(previousStatus);
        return;
      }

      const documentIds = selectedFiles.map(file => currentUploaded.get(file) as string);

      const verifyResult = await verifyDocuments({
        input: {
          applicationId: trackingId,
          titleSearchOwnerId: owner.id,
          documentIds,
        },
      });

      const { validation, statusCode } = verifyResult.verifyTitleOwnerIncomeDocuments;

      if (validation === 'Passed') {
        updateStatus('passed');
        setVerifyErrorCode(null);
        datadogRum.addAction('LandOwner Document Verified', { trackingId });
      } else {
        const mapped = statusCode ? NOA_VERIFICATION_ERROR_CONTENT[statusCode as NoaVerificationErrorCode] : undefined;
        const resolvedCode = mapped ? (statusCode as NoaVerificationErrorCode) : 'DOCUMENT_UNREADABLE';
        setVerifyErrorCode(resolvedCode);

        if (previousStatus === 'failed_twice') {
          updateStatus('failed_final');
          datadogRum.addAction('LandOwner Document Verify Final Failed', {
            trackingId,
            errorCode: resolvedCode,
          });
        } else if (previousStatus === 'failed_once') {
          updateStatus('failed_twice');
        } else {
          updateStatus('failed_once');
        }
      }

      setSelectedFiles([]);
      setUploadedDocIds(new Map());
    } catch {
      setUploadError('Something went wrong uploading your document. Please try again.');
      datadogRum.addAction('LandOwner Document Upload Error', { trackingId });
      updateStatus(previousStatus);
    }
  };

  const isUploading = status === 'uploading';
  const hasFinallyFailed = status === 'failed_final';
  const hasPassed = status === 'passed';
  const isUploadDisabled = hasPassed || hasFinallyFailed || isUploading;
  const disabledDropzoneClassName = css({ pointerEvents: 'none', opacity: 0.5 });

  return (
    <Box background="surfaceLow" borderRadius="medium" padding="large" borderWidth="standard" border="standard">
      <Stack gap="large">
        <Box
          display="flex"
          flexDirection={{ mobile: 'column', tablet: 'row' }}
          justifyContent={{ mobile: 'start', tablet: 'spaceBetween' }}
          alignItems={{ mobile: 'start', tablet: 'center' }}
          gap={{ mobile: 'large', tablet: 'medium' }}
        >
          <Box
            display="flex"
            flexDirection={{ mobile: 'column', tablet: 'row' }}
            alignItems={{ mobile: 'start', tablet: 'center' }}
            gap={{ mobile: 'xsmall', tablet: 'medium' }}
          >
            <Box
              borderRadius="full"
              background={avatarBackground}
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{ width: 40, height: 40 }}
            >
              <Text weight="semibold" size="small">
                {getInitials(owner.fullName)}
              </Text>
            </Box>
            <Text weight="semibold">{owner.fullName}</Text>
          </Box>
          <Text tone="muted" size="small">
            Declared: {formatMoney(declaredIncome)}
          </Text>
        </Box>

        {hasPassed && (
          <Box
            display="flex"
            alignItems="center"
            gap="small"
            background="positiveLow"
            padding="medium"
            borderRadius="medium"
          >
            <CheckCircleIcon tone="positive" size="small" />
            <Text size="small">Notice of assessment is verified.</Text>
          </Box>
        )}

        {hasFinallyFailed && (
          <Box
            display="flex"
            alignItems="start"
            gap="small"
            background="cautionLow"
            padding="medium"
            borderRadius="medium"
          >
            <Box flexShrink={0} paddingTop="xxsmall">
              <ExclamationIcon tone="caution" size="small" />
            </Box>
            <Stack gap="xsmall">
              <Text size="small">
                We weren't able to read your Notice of Assessment. You can continue through to the application and our
                team will contact you to manually check your eligibility.
              </Text>
            </Stack>
          </Box>
        )}

        {isZeroIncome ? (
          <Stack gap="medium">
            <Checkbox
              checked={zeroIncomeConfirmed}
              onChange={event => {
                const checked = event.target.checked;
                setZeroIncomeConfirmed(checked);
                updateStatus(checked ? 'zero_income_skip' : 'idle');
              }}
            >
              {isApplicant
                ? 'I confirm I have $0 taxable income and do not have a Notice of Assessment.'
                : `I confirm ${owner.fullName} has $0 taxable income and does not have a Notice of Assessment.`}
            </Checkbox>
            {zeroIncomeConfirmed && (
              <Box
                display="flex"
                alignItems="start"
                gap="small"
                background="cautionLow"
                padding="medium"
                borderRadius="medium"
              >
                <Box flexShrink={0} paddingTop="xxsmall">
                  <ExclamationIcon tone="caution" size="small" />
                </Box>
                <Text size="small">
                  We understand you don&apos;t have a Notice of Assessment. You can continue through to the application
                  and our team will contact you to manually check your eligibility.
                </Text>
              </Box>
            )}
          </Stack>
        ) : (
          <>
            <Stack gap="medium">
              {verifyErrorCode &&
                !isUploadDisabled &&
                (() => {
                  const errorContent = NOA_VERIFICATION_ERROR_CONTENT[verifyErrorCode];
                  return (
                    <Alert tone="critical">
                      <Stack gap="xsmall">
                        {errorContent.description && <Text size="small">{errorContent.description}</Text>}
                      </Stack>
                    </Alert>
                  );
                })()}

              {uploadError && <Alert tone="critical">{uploadError}</Alert>}

              {tooManyFilesError && (
                <Alert tone="critical" heading="Too many files selected">
                  <Text size="small">
                    You can only upload up to {MAX_FILES} files at once. Please select fewer files and try again.
                  </Text>
                </Alert>
              )}

              <Box
                marginY="large"
                aria-disabled={isUploadDisabled}
                className={isUploadDisabled ? disabledDropzoneClassName : undefined}
              >
                <Field label="Upload Notice of Assessment" disabled={isUploadDisabled}>
                  <Dropzone
                    key={isUploadDisabled ? 'disabled' : 'active'}
                    accept={{ 'image/*': ['.jpeg', '.jpg', '.png'], 'application/pdf': ['.pdf'] }}
                    onChange={isUploadDisabled ? undefined : handleFileDrop}
                    maxFileSizeKb={Math.floor(MAX_FILE_SIZE_BYTES / 1024)}
                    maxFiles={0}
                    multiple={true}
                  />
                  <Text size="xsmall" tone="muted">
                    You can upload up to {MAX_FILES} files at once.
                  </Text>
                </Field>
              </Box>

              {selectedFiles.length > 0 && !hasPassed && !hasFinallyFailed && (
                <Box display="flex" justifyContent="center">
                  <Button onClick={handleUploadAndVerify} loading={isUploading} disabled={isUploading}>
                    Upload and verify
                  </Button>
                </Box>
              )}
            </Stack>

            {hasPassed && (
              <Checkbox
                checked={consent}
                onChange={event => {
                  setConsent(event.target.checked);
                  onConsentChange(owner.id, event.target.checked);
                }}
              >
                {isApplicant
                  ? 'I consent to Brighte using this document to verify my income for NSW Government Loan eligibility purposes.'
                  : `I confirm I am authorised to upload documents on behalf of ${owner.fullName}, and that ${owner.fullName.split(' ')[0]} consents to Brighte verifying their income for this application.`}
              </Checkbox>
            )}
          </>
        )}
      </Stack>
    </Box>
  );
}

function VerifyDeclaredIncome({
  owners,
  declaredIncomes,
  trackingId,
  productName,
  applicantName,
  onConfirm,
  onBack,
}: VerifyDeclaredIncomeProps) {
  const [consents, setConsents] = useState<Record<string, boolean>>({});
  const [ownerStatuses, setOwnerStatuses] = useState<Record<string, OwnerVerificationStatus>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutateAsync: submitIncome } = useSubmitTitleOwnerIncomeMutation({ useErrorBoundary: false });

  const handleConsentChange = useCallback((ownerId: string, consented: boolean) => {
    setConsents(prev => ({ ...prev, [ownerId]: consented }));
  }, []);

  const handleStatusChange = useCallback((ownerId: string, ownerStatus: OwnerVerificationStatus) => {
    setOwnerStatuses(prev => ({ ...prev, [ownerId]: ownerStatus }));
  }, []);

  const anyOwnerFinallyFailed = owners.some(owner => ownerStatuses[owner.id] === 'failed_final');

  const allConsented =
    anyOwnerFinallyFailed ||
    owners.every(
      owner =>
        ownerStatuses[owner.id] === 'failed_final' ||
        ownerStatuses[owner.id] === 'zero_income_skip' ||
        consents[owner.id] === true,
    );
  const allOwnersDone =
    anyOwnerFinallyFailed ||
    owners.every(owner => {
      const ownerStatus = ownerStatuses[owner.id];
      return ownerStatus === 'passed' || ownerStatus === 'failed_final' || ownerStatus === 'zero_income_skip';
    });
  const canContinue = allConsented && allOwnersDone;

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await submitIncome({
        input: {
          applicationId: trackingId,
          owners: owners.map(owner => {
            const annualIncome = Number(declaredIncomes[owner.id] ?? 0);
            const monthlyIncome = annualIncome / 12;
            const ownerStatus = ownerStatuses[owner.id];
            const isUnverified =
              ownerStatus === 'failed_final' ||
              ownerStatus === 'zero_income_skip' ||
              (anyOwnerFinallyFailed && ownerStatus !== 'passed');
            return {
              id: owner.id,
              declaredIncome: monthlyIncome,
              ...(isUnverified ? { isIncomeVerified: false } : {}),
            };
          }),
        },
      });
      onConfirm();
    } catch {
      datadogRum.addError(new Error('HESL income submission failed'), {
        trackingId,
        ownerCount: owners.length,
      });
      datadogRum.addAction('LandOwner Income Submit Error', { trackingId, ownerCount: owners.length });
      onConfirm();
    }
    setIsSubmitting(false);
  };

  return (
    <Stack padding="xlarge" gap="xlarge">
      <EligibilityStepHeader currentStep={5} totalSteps={5} productName={productName} />

      <Stack gap="xlarge">
        <Box className={css({ lineHeight: '1.3em' })}>
          <Heading level="1">Upload documents to verify your income</Heading>
        </Box>

        <Text size="standard">
          We need a recent{' '}
          <Text as="span" weight="semibold" inline>
            Notice of Assessment (FY24 or FY25)
          </Text>{' '}
          from each person on title to verify your declared income.
        </Text>

        {owners.map((owner, index) => (
          <OwnerVerificationCard
            key={owner.id}
            owner={owner}
            declaredIncome={declaredIncomes[owner.id] ?? '0'}
            trackingId={trackingId}
            avatarBackground={AVATAR_BACKGROUNDS[index] ?? 'infoLow'}
            isApplicant={owner.fullName.trim().toLowerCase() === applicantName.trim().toLowerCase()}
            onConsentChange={handleConsentChange}
            onStatusChange={handleStatusChange}
          />
        ))}

        <Box>
          <Accordion type="multiple">
            <AccordionItem value="q0" label="Why do I need to upload my Notice of Assessment?" level="4">
              <Text>
                The NSW Government requires income verification for this program. Your Notice of Assessment is an
                official ATO document that confirms your taxable income. We compare it against the figure you declared
                in the previous step to verify your household qualifies.
              </Text>
            </AccordionItem>
            <AccordionItem value="q1" label="I don't have the documents needed right now" level="4">
              <Text>
                No problem, your progress has been saved. You can return to this step once you have your documents ready
                by clicking the link in your invite email.
              </Text>
            </AccordionItem>
            <AccordionItem value="q2" label="Where can I find my Notice of Assessment?" level="4">
              <Text>
                You can download a copy of your Notice of Assessment in{' '}
                <TextLink href="https://my.gov.au/" tone="primary" target="_blank" rel="noopener noreferrer">
                  MyGov
                </TextLink>
                .
              </Text>
            </AccordionItem>
          </Accordion>
        </Box>

        <Button tone="primary" onClick={handleConfirm} disabled={!canContinue} loading={isSubmitting}>
          Continue to application
        </Button>

        <Box alignSelf="start">
          <Button prominence="none" tone="neutral" onClick={onBack}>
            ← Back
          </Button>
        </Box>
      </Stack>
    </Stack>
  );
}

export default VerifyDeclaredIncome;
